import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import {
  LayoutDashboard,
  Package,
  Store as StoreIcon,
  Route as RouteIcon,
  Users,
  ShoppingCart,
  ClipboardList,
  BarChart3,
  MapPin,
  IndianRupee,
  LogOut,
  Menu,
  DatabaseBackup,
} from "lucide-react";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/live", label: "Live map", icon: MapPin },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/ledger", label: "Ledger", icon: IndianRupee },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/attendance", label: "Attendance", icon: ClipboardList },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/stores", label: "Stores", icon: StoreIcon },
  { to: "/admin/routes", label: "Routes", icon: RouteIcon },
  { to: "/admin/salesmen", label: "Salesmen", icon: Users },
  { to: "/admin/backup", label: "Backup", icon: DatabaseBackup },
];

function NavList({ onNavigate }) {
  return (
    <nav className="flex-1 py-6 px-3 space-y-1" data-testid="admin-nav-list">
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              isActive
                ? "bg-white/10 text-white"
                : "text-white/70 hover:text-white hover:bg-white/5"
            }`
          }
          data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <n.icon className="h-4 w-4" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const signOut = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[color:var(--bg-base)]" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-[#1C1A17] text-[#FAF9F6] sticky top-0 h-screen">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-white/10">
          <img src="/kridam-logo.png" alt="GM Kridam Foods" className="h-12 w-auto" />
          <div>
            <div className="font-display font-bold tracking-tight">GM Kridam Foods</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-white/50">Admin console</div>
          </div>
        </div>
        <NavList />
        <div className="p-4 border-t border-white/10">
          <div className="text-sm font-medium">{user?.name}</div>
          <div className="text-xs text-white/50">{user?.email}</div>
          <button
            onClick={signOut}
            className="mt-3 flex items-center gap-2 text-sm text-white/70 hover:text-white"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#1C1A17] text-white px-3 h-14 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="p-2 -ml-1 rounded-md hover:bg-white/10 active:bg-white/20"
                aria-label="Open menu"
                data-testid="admin-mobile-menu-trigger"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-72 p-0 bg-[#1C1A17] text-[#FAF9F6] border-r border-white/10 flex flex-col"
              data-testid="admin-mobile-drawer"
            >
              <div className="px-6 py-6 flex items-center gap-3 border-b border-white/10">
                <img src="/kridam-logo.png" alt="GM Kridam Foods" className="h-10 w-auto" />
                <div>
                  <div className="font-display font-bold tracking-tight">GM Kridam Foods</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-white/50">
                    Admin console
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <NavList onNavigate={() => setMobileOpen(false)} />
              </div>
              <div className="p-4 border-t border-white/10">
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-white/50 truncate">{user?.email}</div>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    signOut();
                  }}
                  className="mt-3 flex items-center gap-2 text-sm text-white/70 hover:text-white"
                  data-testid="admin-mobile-drawer-logout"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <Link to="/admin" className="flex items-center gap-2 font-display font-bold">
            <img src="/kridam-logo.png" alt="GM Kridam Foods" className="h-8 w-auto" />
            <span className="text-sm">GM Kridam Foods</span>
          </Link>
        </div>
        <button
          onClick={signOut}
          className="p-2 -mr-1 rounded-md hover:bg-white/10 active:bg-white/20"
          aria-label="Sign out"
          data-testid="admin-mobile-logout"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
